import { useFormik } from "formik";
import { Button } from "primereact/button";
import React from "react";
import { useFragment, useMutation } from "react-relay";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useTranslation } from "@i18n/hooks";
import { type editExternalLicenseForm_EditExternalLicenseMutation } from "@relay/editExternalLicenseForm_EditExternalLicenseMutation.graphql";
import { type editExternalLicenseForm_ExternalLicenseFragment$key } from "@relay/editExternalLicenseForm_ExternalLicenseFragment.graphql";
import {
	EDIT_EXTERNAL_LICENSE_MUTATION,
	EXTERNAL_LICENSE_FRAGMENT,
} from "./edit-external-license-form.graphql";
import {
	type EditExternalLicenseFormFormState,
	type EditExternalLicenseFormProps,
} from "./edit-external-license-form.interface";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { ValidatedField } from "../../../../components/ValidatedField";
import { WarningUnsavedChangesDialog } from "../../../../components/WarningUnsavedChangesDialog";
import { useCallbackPrompt } from "../../../../hooks/UseCallBackPrompt";
import { Paths } from "../../../../routes/paths";
import {
	addEditedFormToEditedFormsArray,
	resetArrayOfEditedForms,
	selectArrayOfEditedForms,
} from "../../../../store/slices/CoreSlice";
import { useTypedDispatch, useTypedSelector } from "../../../../store/store.redux";

export const EditExternalLicenseForm = ({ loading, entityRef }: EditExternalLicenseFormProps) => {
	const { t } = useTranslation(["common", "validation"]);
	const navigate = useNavigate();
	const dispatch = useTypedDispatch();
	const arrayOfEditedForms = useTypedSelector(selectArrayOfEditedForms);
	const [showPrompt, confirmNavigation, cancelNavigation, setShowPrompt] = useCallbackPrompt(
		arrayOfEditedForms.length > 0,
	);

	const [updateExternalLicense] =
		useMutation<editExternalLicenseForm_EditExternalLicenseMutation>(
			EDIT_EXTERNAL_LICENSE_MUTATION,
		);

	const externalLicense = useFragment<editExternalLicenseForm_ExternalLicenseFragment$key>(
		EXTERNAL_LICENSE_FRAGMENT,
		entityRef,
	);

	const onSubmit = (values: EditExternalLicenseFormFormState) => {
		updateExternalLicense({
			variables: {
				input: {
					externalLicenseId: externalLicense.id!,
					data: {
						name: values.name!,
						code: values.code!,
					},
				},
			},
		});
	};

	const formId = "EditExternalLicenseForm";
	const formik = useFormik<EditExternalLicenseFormFormState>({
		initialValues: {
			name: externalLicense.data.name ?? t("common:externalLicenses.newLicense"),
			code: externalLicense.data?.code ?? "",
		},
		validationSchema: Yup.object().shape({
			name: Yup.string().required(t("validation:externalLicenseNameRequired")),
			code: Yup.string().required(t("validation:externalLicenseCodeRequired")),
			usageInformation: Yup.string().optional(),
			validUntil: Yup.string().optional(),
			daysOfValidityAfterIssuing: Yup.number().optional(),
		}),
		onSubmit: (values, { setSubmitting }) => {
			onSubmit({
				name: values.name,
				code: values.code,
			});
			setSubmitting(false);
			dispatch(resetArrayOfEditedForms());
		},
	});

	return (
		<>
			{showPrompt && (
				<WarningUnsavedChangesDialog
					confirmNavigation={confirmNavigation}
					setShowDialog={setShowPrompt}
					cancelNavigation={cancelNavigation}
				/>
			)}
			<form onSubmit={formik.handleSubmit} className="p-fluid">
				<ValidatedField<EditExternalLicenseFormFormState, string>
					name={"name"}
					label={t("common:table.name")}
					component={DefaultTextFieldComponent}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					formikConfig={formik}
				/>
				<ValidatedField<EditExternalLicenseFormFormState, string>
					name={"code"}
					label={t("common:externalLicenses.code")}
					component={DefaultTextFieldComponent}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					formikConfig={formik}
				/>
				<Button
					disabled={loading}
					type="submit"
					label={t("common:actions.save")}
					className="mt-2"
				/>
				<Button
					disabled={loading}
					label={t("common:actions.back")}
					onClick={() => {
						navigate(Paths.externalLicensePools.withId(externalLicense.pool?.id!).path);
					}}
					className="mt-2"
				/>
			</form>
		</>
	);
};
